/** Cartão compacto de uma ferramenta trabalhando, entre a pergunta e a
 *  resposta: ícone, rótulo e estado. Entra com fade-down; a troca de girando
 *  para concluído é uma troca de cor. Ao terminar a resposta, a lista recolhe
 *  para a linha "N etapas", que abre de novo com um clique. */
import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'
import { BookOpen, Check, ChevronDown, Database, CircleDot, Flag } from 'lucide-react'
import { useI18n } from '@/core/i18n/useI18n'
import type { EtapaDaResposta } from './api'

function IconeDaEtapa({ id }: { id: string }) {
  const chave = id.toLowerCase()
  if (chave.includes('conhec') || chave.includes('regra')) return <BookOpen size={14} strokeWidth={2} />
  if (chave.includes('sql') || chave.includes('consult')) return <Database size={14} strokeWidth={2} />
  if (chave.includes('lacuna')) return <Flag size={14} strokeWidth={2} />
  return <CircleDot size={14} strokeWidth={2} />
}

/** O rótulo vem do servidor, que sabe o que a ferramenta está fazendo; sem ele,
 *  a tradução pelo id. */
function rotuloDaEtapa(t: ReturnType<typeof useI18n>['t'], etapa: EtapaDaResposta): string {
  if (etapa.rotulo) return etapa.rotulo
  const chave = `assistente.etapa.${etapa.id}`
  const traduzido = t(chave)
  return traduzido === chave ? etapa.id : traduzido
}

export function EtapaCartao({ etapa }: { etapa: EtapaDaResposta }) {
  const { t } = useI18n()
  const concluida = etapa.estado === 'concluida'
  return (
    <Box
      className="anim-fade-down"
      role="status"
      aria-label={`${rotuloDaEtapa(t, etapa)}, ${t(concluida ? 'assistente.etapa.concluida' : 'assistente.etapa.andamento')}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 0.75,
        borderRadius: '0 12px 0 12px',
        border: '1px solid',
        borderColor: 'divider',
        color: concluida ? 'text.secondary' : 'text.primary',
        fontSize: '0.8125rem',
        transition: 'color var(--transition-quick), border-color var(--transition-quick)',
      }}
    >
      <Box sx={{ display: 'flex', color: concluida ? 'text.disabled' : 'primary.main', flex: 'none' }}>
        <IconeDaEtapa id={etapa.id} />
      </Box>
      <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {rotuloDaEtapa(t, etapa)}
      </Typography>
      <Box sx={{ display: 'flex', flex: 'none', color: concluida ? 'success.main' : 'primary.main', transition: 'color var(--transition-quick)' }}>
        {concluida ? <Check size={14} strokeWidth={2.4} aria-hidden /> : <CircularProgress size={12} thickness={5} color="inherit" />}
      </Box>
    </Box>
  )
}

/** A lista das etapas de uma resposta. Enquanto a resposta chega fica aberta;
 *  ao fim recolhe numa linha só. */
export function EtapasDaResposta({ etapas, emAndamento }: { etapas: EtapaDaResposta[]; emAndamento: boolean }) {
  const { t } = useI18n()
  const [aberta, setAberta] = useState(emAndamento)

  useEffect(() => {
    if (emAndamento) setAberta(true)
    else setAberta(false)
  }, [emAndamento])

  if (etapas.length === 0) return null

  return (
    <Box sx={{ mb: 1 }}>
      {!emAndamento && (
        <ButtonBase
          onClick={() => setAberta((a) => !a)}
          aria-expanded={aberta}
          aria-label={t(aberta ? 'assistente.etapas.ocultar' : 'assistente.etapas.mostrar')}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.375,
            ml: -0.75,
            borderRadius: 1.5,
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 600,
            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            '&.Mui-focusVisible': { boxShadow: 'var(--focus-ring)' },
          }}
        >
          <Check size={13} strokeWidth={2.4} aria-hidden />
          <span>{t('assistente.etapas.contagem', { count: etapas.length })}</span>
          <ChevronDown
            size={13}
            aria-hidden
            style={{ transform: aberta ? 'none' : 'rotate(-90deg)', transition: 'transform var(--transition-base)' }}
          />
        </ButtonBase>
      )}
      <Collapse in={aberta} timeout={200}>
        <Box sx={{ display: 'grid', gap: 0.75, pt: emAndamento ? 0 : 0.75 }}>
          {etapas.map((etapa) => <EtapaCartao key={etapa.id} etapa={etapa} />)}
        </Box>
      </Collapse>
    </Box>
  )
}
